import { inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslocoService } from '@jsverse/transloco';
import { map, switchMap } from 'rxjs';

/**
 * Text of a key from a feature scope as a signal that follows the active language. Use it for
 * text built in TypeScript (option labels, computed messages); templates use the `transloco` pipe.
 *
 * `translateSignal()` of Transloco prefixes the key with the scope alias again when the component
 * provides a scope, which clashes with the full keys (`adminToolWizard.title.create`) that the
 * catalogs and `npm run check:i18n` use, so the scope is loaded here and the key is read as written.
 * Call it from an injection context (a field initializer or constructor).
 *
 * @param scope Scope path the key belongs to, such as `admin/tool-wizard`.
 * @param key Full key including the scope alias.
 * @param params Values for the message arguments.
 * @returns The translated text; empty until the scope is loaded.
 * @example
 * private readonly title = scopedTranslate('admin/tool-wizard', 'adminToolWizard.title.create');
 */
export function scopedTranslate(
  scope: string,
  key: string,
  params?: Readonly<Record<string, unknown>>,
): Signal<string> {
  const transloco = inject(TranslocoService);
  const text$ = transloco.langChanges$.pipe(
    switchMap((lang) =>
      transloco.load(`${scope}/${lang}`).pipe(map(() => transloco.translate(key, params, lang))),
    ),
  );
  return toSignal(text$, { initialValue: '' });
}
